/**
 * Factory for creating page-scoped filter view stores
 * Each page (Projects, Customers, etc.) gets its own isolated store instance
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface FilterViewStoreState {
  // State
  pageIdentifier: string;
  filterPanelOpen: boolean;
  selectedViewId: string | null;

  // Actions
  setFilterPanelOpen: (open: boolean) => void;
  toggleFilterPanel: () => void;
  setSelectedViewId: (viewId: string | null) => void;
  reset: () => void;
}

/**
 * Creates a new filter view store for a specific page
 * @param pageIdentifier - Unique identifier for the page (e.g., 'ProjectPortfolioOverview')
 * @param initialFilterPanelOpen - Initial state of the filter panel (default: false)
 */
export function createFilterViewStore(
  pageIdentifier: string,
  initialFilterPanelOpen = false,
) {
  return create<FilterViewStoreState>()(
    devtools(
      (set) => ({
        // Initial state
        pageIdentifier,
        filterPanelOpen: initialFilterPanelOpen,
        selectedViewId: null,

        // Actions
        setFilterPanelOpen: (open) => set({ filterPanelOpen: open }),

        toggleFilterPanel: () =>
          set((state) => ({ filterPanelOpen: !state.filterPanelOpen })),

        setSelectedViewId: (viewId) => set({ selectedViewId: viewId }),

        reset: () =>
          set({
            filterPanelOpen: initialFilterPanelOpen,
            selectedViewId: null,
          }),
      }),
      {
        name: `FilterViewStore:${pageIdentifier}`,
      },
    ),
  );
}
